import { useCallback } from 'react';
import {
  generatePath,
  NavigateOptions,
  PathParam,
  useNavigate as reactRouterUseNavigate,
} from 'react-router-dom';

import { QUERY_STRINGS, QueryParamType } from '@routes/routes.types';

type NavigateFunctionProps<T extends string> =
  | [
      to: T,
      options?: NavigateOptions & {
        params?: {
          [key in PathParam<T>]: string | null;
        };
        query?: Partial<{ [key in QUERY_STRINGS]: QueryParamType<key> }>;
        hash?: string;
      },
    ]
  | [to: number];

/** Wrapper over `react-router-dom` useNavigate hook with application specific typing */
export const useNavigate = () => {
  const navigate = reactRouterUseNavigate();

  return useCallback(<T extends string>(...props: NavigateFunctionProps<T>) => {
    const [toProp, options] = props;

    const { params, query, hash, ...restOptions } = options ?? {};

    if (typeof toProp === 'number') {
      return navigate(toProp);
    }

    const url = new URL(location.origin + toProp);

    // If url string itself includes param or hash, use it directly
    // instead of using `options.query` and `options.hash`
    if (!!url.search || !!url.hash) {
      return navigate(toProp, restOptions);
    }

    // Apply url params
    const to = params ? generatePath(toProp, params) : toProp;

    const searchParams = new URLSearchParams(query);

    navigate(
      { pathname: to, search: searchParams.toString(), hash },
      restOptions
    );
  }, []);
};
